import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button, Checkbox, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/overlays';
import { addDaysLocal, todayLocalDate } from '@/lib/utils';
import { FOLLOW_UP_PRIORITIES, FOLLOW_UP_TYPES } from '@shared/enums.js';

/**
 * Follow-up scheduling. A follow-up is date-only unless the user explicitly
 * adds a time — that split is what keeps due dates stable across DST.
 */
export function FollowUpDialog({
  open,
  onOpenChange,
  leadId,
  defaultType,
  defaultDate,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: number;
  defaultType?: string;
  defaultDate?: string;
  onCreated?: () => void;
}) {
  const queryClient = useQueryClient();
  const [type, setType] = React.useState(defaultType ?? 'Call');
  const [date, setDate] = React.useState(defaultDate ?? addDaysLocal(todayLocalDate(), 3));
  const [useTime, setUseTime] = React.useState(false);
  const [time, setTime] = React.useState('09:00');
  const [priority, setPriority] = React.useState('Normal');
  const [notes, setNotes] = React.useState('');
  const submitLock = React.useRef(false);

  React.useEffect(() => {
    if (open) {
      setType(defaultType ?? 'Call');
      setDate(defaultDate ?? addDaysLocal(todayLocalDate(), 3));
      setUseTime(false);
      setTime('09:00');
      setPriority('Normal');
      setNotes('');
      submitLock.current = false;
    }
  }, [open, defaultType, defaultDate]);

  const create = useMutation({
    mutationFn: () =>
      api.createFollowUp({
        leadId,
        followUpType: type,
        dueDate: date,
        dueTime: useTime ? time : null,
        priority,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['follow-ups'] });
      toast.success('Follow-up scheduled');
      onOpenChange(false);
      onCreated?.();
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => {
      submitLock.current = false;
    },
  });

  const submit = () => {
    if (submitLock.current || create.isPending) return;
    if (!date || date < todayLocalDate()) {
      toast.error('Follow-up date cannot be in the past.');
      return;
    }
    submitLock.current = true;
    create.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Schedule a follow-up</DialogTitle>
          <DialogDescription>
            Without a time this is an all-day reminder on that calendar date.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <Field label="Type">
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              {FOLLOW_UP_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Due date">
            <Input type="date" min={todayLocalDate()} value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>

          <div className="flex flex-wrap gap-1.5">
            {[
              { label: 'Tomorrow', days: 1 },
              { label: 'In 3 days', days: 3 },
              { label: 'Next week', days: 7 },
              { label: 'In 2 weeks', days: 14 },
              { label: 'Next month', days: 30 },
            ].map((preset) => (
              <Button
                key={preset.label}
                variant="outline"
                size="xs"
                onClick={() => setDate(addDaysLocal(todayLocalDate(), preset.days))}
              >
                {preset.label}
              </Button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={useTime} onCheckedChange={(v) => setUseTime(!!v)} />
            Set a specific time
          </label>
          {useTime && (
            <Field label="Time">
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </Field>
          )}

          <Field label="Priority">
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              {FOLLOW_UP_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Notes">
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button size="sm" loading={create.isPending} disabled={create.isPending || !date || date < todayLocalDate()} onClick={submit}>
            Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
